import React, { useState, useEffect, createContext, useContext, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress } from '@mui/material';
import Header from './components/Header';
import Home from './pages/Home';
import TripDetail from './pages/TripDetail';
import AddTrip from './pages/AddTrip';
import AddEditActivity from './pages/AddEditActivity';
import ActivityDetail from './pages/ActivityDetail';
import Admin from './pages/Admin';
import AdminUsers from './pages/AdminUsers';
import Profile from './pages/Profile';
import Login from './pages/Login';
import { getUser } from './services/auth';
import { getMe } from './services/api';

const DARK_MODE_KEY = 'tripvibe_darkmode';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const DarkModeContext = createContext(null);
export const useDarkMode = () => useContext(DarkModeContext);

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem(DARK_MODE_KEY);
    return stored === 'true';
  });

  useEffect(() => {
    getUser().then(async (u) => {
      if (u) {
        try {
          const { data } = await getMe();
          setUser({ ...u, ...data });
        } catch {
          setUser(u);
        }
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem(DARK_MODE_KEY, String(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode((prev) => !prev);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? 'dark' : 'light',
          primary: { main: '#1565c0' },
          secondary: { main: '#ff6b35' },
          ...(darkMode
            ? { background: { default: '#121212', paper: '#1e1e1e' } }
            : { background: { default: '#f5f5f5' } }),
        },
      }),
    [darkMode],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthContext.Provider value={{ user, setUser }}>
        <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
          <BrowserRouter>
            {!user ? (
              <Routes>
                <Route path="*" element={<Login />} />
              </Routes>
            ) : (
              <>
                <Header />
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/trips/new" element={<AddTrip />} />
                  <Route path="/trips/:tripId" element={<TripDetail />} />
                  <Route path="/trips/:tripId/activities/new" element={<AddEditActivity />} />
                  <Route path="/trips/:tripId/activities/:activityId/edit" element={<AddEditActivity />} />
                  <Route path="/trips/:tripId/activities/:activityId" element={<ActivityDetail />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </>
            )}
          </BrowserRouter>
        </DarkModeContext.Provider>
      </AuthContext.Provider>
    </ThemeProvider>
  );
}
